import { ExtraBase, QuoteExtraApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { CustomsDuties } from '../CustomsDuties';
import { IossNumber, IossNumberSource } from '~/apps/smartsend/lib/consts/ItemMetaConstants';
import { SmartSendProvider } from '~/lib/consts/providers';
import { ddpQuoteKey } from '~/lib/api/smartsend/cache-keys';
import { IossIsRequired } from '~/apps/smartsend/lib/helpers/IossUtils';
import { Suspense, useCallback, useContext, useMemo, useRef, useState } from 'react';
import GetDeliveredDutyShippingCost from '~/apps/smartsend/lib/helpers/GetDeliveredDutyShippingCost';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { DeliveredDutyPaidExtra } from '../../ExtrasHelper';
import useOnLoad from '~/routes/use-on-load';
import { getSelectedQuoteOption } from '../generateProtectionKey';
import { P2GBox, P2GButton } from '@parcel2go/core-ui';
import { SaveItemsAction } from '~/apps/smartsend/lib/context/SmartSendContextModels';
import { ApplierOption } from '~/apps/smartsend/lib/helpers/ApplierOption';
import { ExtrasHaveChanged, MetaHasChanged } from '~/apps/smartsend/lib/helpers/SmartSendSocketHelper';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';
import { EventSections } from '~/apps/smartsend/lib/events/EventLogger';
import P2GContentDropDown from '../../P2GContentDropDown';
import Loading from '~/components/loading/Loading';
import { uid } from '~/apps/smartsend/lib/helpers/uid';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { ToSmartSendAnalyticsModel, ToSmartSendAnalyticsPatchModel } from '../quotes/utils/CurrentSelectionType';
import { getDeclaredContents } from '~/apps/smartsend/lib/parcels/itemView';

type propsInner = {
  item: SmartSendItemApiModel;
  onSave: () => void;
};

type props = {
  item: SmartSendItemApiModel;
  tooltipContent: React.ReactNode;
  tooltipFooterContent?: React.ReactNode;
  control: React.ReactNode;
};

const ItemCustomsDropDown = (props: props) => {
  const [contentKey, setContentKey] = useState(uid());
  return (
    <P2GContentDropDown placement="bottom-end" key={contentKey} control={props.control}>
      <Suspense>
        <P2GBox>{props.tooltipContent}</P2GBox>
        <ItemCustomersDropDownInner item={props.item} onSave={() => setContentKey(uid())} />
        {props.tooltipFooterContent && <P2GBox>{props.tooltipFooterContent}</P2GBox>}
      </Suspense>
    </P2GContentDropDown>
  );
};

const ItemCustomersDropDownInner = (props: propsInner) => {
  const { getDeliveredDutyPaidCalculation, getQuoteWithValidation, getQuoteOptions } = useSmartSendApi();
  const { dispatch } = useContext(SmartSendContext);
  const { track } = useSmartSendAnalytics();
  const [selectedType, setSelectedType] = useState<'DDP' | 'DDU' | 'IOSS'>(null);
  const [meta, setMeta] = useState(JSON.parse(JSON.stringify({ ...props.item.Meta })));
  const [validation, setValidation] = useState(null);
  const [availableExtras, setAvailableExtras] = useState<QuoteExtraApiModel[]>([]);
  const [extras, setExtras] = useState<ExtraBase[]>([]);
  const [loading, setLoading] = useState(true);
  const originalItemSnapshot = useRef<SmartSendItemApiModel>(JSON.parse(JSON.stringify(props.item)));

  const getData = useCallback(async () => {
    setLoading(true);

    const v = await getQuoteWithValidation(props.item);
    if (v.success) {
      setValidation(v.result);
    }

    // Needs a reshuffle TODO
    const q = await getQuoteOptions(
      props.item.Service.Provider,
      props.item.Service.AccountId,
      props.item,
      props.item.Service.Id
    );

    if (q.success) {
      const qo = getSelectedQuoteOption(props.item, q.result.Quotes[0]) || q.result.Quotes?.[0]?.Options?.[0];
      const include = [DeliveredDutyPaidExtra];
      setAvailableExtras(qo.AvailableExtras.concat(qo.Extras).filter((x) => include.includes(x.Id)));
      setExtras(qo.AvailableExtras.concat(qo.Extras).filter((x) => props.item.Extras.map((e) => e.Id).includes(x.Id)));
    }
    setLoading(false);
  }, [props.item, getQuoteWithValidation, getQuoteOptions]);

  const dduUnavailable = useMemo(
    () =>
      (validation?.Quote?.Service?.Tags?.includes('dduUnavailable') ||
        validation?.Quote?.Tags?.includes('dduUnavailable')) ??
      false,
    [validation]
  );

  const getDdpCalc = useCallback(async () => {
    const option = props.item.State?.validation?.QuoteState?.Quote?.Options?.[0];
    const shippingCost = GetDeliveredDutyShippingCost(option);
    try {
      const calculation = await getDeliveredDutyPaidCalculation(
        props.item.Service.Provider,
        props.item.Service.AccountId,
        props.item,
        shippingCost
      );
      return calculation.success ? calculation.result : null;
    } catch {
      return null;
    }
  }, [getDeliveredDutyPaidCalculation, props.item]);

  const onIossUpdate = useCallback((v: string) => {
    setMeta((m) => ({
      ...m,
      [IossNumber]: { Type: 'String', Value: v }
    }));
  }, []);

  const onToggleUpdate = useCallback((extra: ExtraBase, include: boolean) => {
    if (!extra) {
      return;
    }

    setExtras((prev) => {
      const hasExtra = prev.some((x) => x.Id === extra.Id);
      if (include) {
        return hasExtra ? prev : [...prev, extra];
      }
      return hasExtra ? prev.filter((x) => x.Id !== extra.Id) : prev;
    });
  }, []);

  const onTabChange = useCallback(() => {}, []);

  const wrapper = useCallback((x: React.ReactNode) => <>{x}</>, []);

  const onSave = useCallback(() => {
    const actions: ApplierOption[] = [];

    if (ExtrasHaveChanged(props.item.Extras, extras)) {
      actions.push('Extras');
    }

    if (MetaHasChanged(props.item.Meta, meta)) {
      actions.push('Meta');
    }

    if (actions.length > 0) {
      const patchModel = ToSmartSendAnalyticsPatchModel(
        { Extras: extras.map((e) => e.Id) },
        actions.filter((a) => a === 'Extras')
      );
      track({
        type: 'item_edited',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.SmartSendItem]),
          surface: 'item-customs-dropdown',
          action: 'save-item-edits',
          changedFields: actions,
          itemScope: 'single',
          ...(actions.includes('Extras') && {
            extraIds: extras.map((e) => e.Id)
          }),
          originalItems: [ToSmartSendAnalyticsModel(originalItemSnapshot.current)],
          patchModel: patchModel
        }
      });

      dispatch(
        new SaveItemsAction([props.item.Id], actions, {
          Extras: extras,
          Meta: meta
        })
      );
    }

    props.onSave?.();
  }, [dispatch, extras, meta, props, track]);

  useOnLoad(() => {
    getData();
  });

  // Matches ItemCustomsPanel: a duty calculation needs a tariff code against everything being
  // declared, and it is the consignment contents that are declared, not the order lines.
  const allDeclaredContentsHaveTariffCodes = useMemo(() => {
    const contents = getDeclaredContents(props.item);
    return contents.length > 0 && contents.every((c) => !!c.TariffCode);
  }, [props.item]);

  const isInvalid = useMemo(() => {
    if (selectedType === 'IOSS' && !meta[IossNumber]?.Value) {
      return true;
    }

    if (selectedType === 'DDP' && !availableExtras?.some((e) => e.Id === DeliveredDutyPaidExtra)) {
      return true;
    }

    if (selectedType === 'DDU' && dduUnavailable) {
      return true;
    }

    return false;
  }, [meta, availableExtras, selectedType, dduUnavailable]);

  if (loading) {
    return <Loading />;
  }

  return (
    <P2GBox sx={{ maxWidth: '650px', minWidth: '300px' }}>
      <CustomsDuties
        ddpQuoteKey={ddpQuoteKey(SmartSendProvider, SmartSendProvider, props.item)}
        currencyCode={validation.Quote?.CurrencyCode ?? props.item.CurrencyCode}
        dduUnavailable={dduUnavailable}
        recipientIsoCode={props.item.Recipient?.CountryIsoCode}
        changeTab={onTabChange}
        canGetDdpCalculation={allDeclaredContentsHaveTariffCodes}
        iossNumber={meta[IossNumber]?.Value}
        onIossUpdate={onIossUpdate}
        toggleExtra={onToggleUpdate}
        iossNumberRequired={IossIsRequired(validation)}
        iossNumberEnabled={!!validation?.ItemValidation.find((x) => x.name === IossNumber)}
        iossNumberValidation={validation?.ItemValidation.find((x) => x.name === IossNumber)?.pattern}
        iossNumberSource={meta[IossNumberSource]?.Value}
        availableExtras={availableExtras}
        extras={extras}
        getDdpCost={getDdpCalc}
        wrapper={wrapper}
        onSelectedOptionChange={setSelectedType}
      />
      <P2GBox mt={2}>
        <P2GButton disabled={isInvalid} variant="primary" onClick={onSave}>
          Save
        </P2GButton>
      </P2GBox>
    </P2GBox>
  );
};
export default ItemCustomsDropDown;
